import { useZoneStore } from '../store/zoneStore';
import { useTabStore } from '../store/tabStore';
import { useNetworkElementsStore } from '../store/networkElementsStore';
import { useNetworkUIStore } from '../store/networkUIStore';
import { useCouplingStore } from '../store/couplingStore';
import { couplingPosition } from '../types/coupling';
import type { Abgangsleitung, AnyNetworkElement, Coordinates } from '../types/network';
import type { MonitoringSubTab } from '../components/monitoring/MonitoringTabBar';
import { MONITORING_SUB_TAB_KEY } from '../utils/monitoringSubTab';
import { switchZone } from './zoneSwitchService';

/** Zoom of a drill-down: the element with its surroundings, not the single marker. */
const FOCUS_ZOOM = 18;
const HIGHLIGHT_MS = 4000;
const COUPLING_WAIT_MS = 4000;

export interface ZoneMapTarget {
  zoneId: string;
  /** Element to select and centre (transformer / cable of an alert, a ranking row). */
  elementId?: string | null;
  /** Trennstelle to select and centre (Vorgänge: coupling off its normal state). */
  couplingId?: string | null;
}

function elementCenter(element: AnyNetworkElement): Coordinates | null {
  if (element.type === 'abgangsleitung') {
    const waypoints = (element as Abgangsleitung).waypoints;
    if (waypoints && waypoints.length > 0) {
      return waypoints[Math.floor(waypoints.length / 2)];
    }
  }
  return element.coordinates ?? null;
}

/** Opens the zone (switching only when it is not the active one) on the given tab. */
async function openZone(zoneId: string, tab: 'map' | 'monitoring'): Promise<boolean> {
  if (useZoneStore.getState().activeZoneId === zoneId) {
    useTabStore.getState().openTab(tab);
    return true;
  }
  const switched = await switchZone(zoneId, tab === 'map' ? { tab, waitForMapResize: true } : { tab });
  // A newer switch may have won while this one was loading
  return switched && useZoneStore.getState().activeZoneId === zoneId;
}

/** The couplings of a freshly selected zone load on their own (couplingStore subscription). */
function waitForCouplings(zoneId: string): Promise<boolean> {
  const ready = () => {
    const s = useCouplingStore.getState();
    return s.zoneId === zoneId && !s.isLoading;
  };
  if (ready()) return Promise.resolve(true);
  return new Promise(resolve => {
    const timer = setTimeout(() => { unsubscribe(); resolve(false); }, COUPLING_WAIT_MS);
    const unsubscribe = useCouplingStore.subscribe(() => {
      if (ready()) {
        clearTimeout(timer);
        unsubscribe();
        resolve(true);
      }
    });
  });
}

/**
 * THE drill-down of the Netzmonitoring page (doc/Architektur/NETZMONITORING.md → Drill-down):
 * whatever describes the state of the grid — an open alert, a ranking row, a data-quality
 * finding, a zone — opens that ONE zone on the live map, with the affected element selected and
 * centred. The map is where the operator sees the context and can act; the zone monitoring is
 * the explicit second step ({@link openZoneMonitoring}).
 */
export async function openZoneOnMap(target: ZoneMapTarget): Promise<void> {
  if (!(await openZone(target.zoneId, 'map'))) return;
  const ui = useNetworkUIStore.getState();

  if (target.elementId) {
    const element = (useNetworkElementsStore.getState().elements as AnyNetworkElement[]).find(e => e.id === target.elementId);
    const center = element ? elementCenter(element) : null;
    if (element && center) {
      ui.setSelectedElement(element);
      ui.setMapView(center, FOCUS_ZOOM);
      ui.setHighlightedElement(element.id);
      setTimeout(() => {
        if (useNetworkUIStore.getState().highlightedElementId === element.id) {
          useNetworkUIStore.getState().setHighlightedElement(null);
        }
      }, HIGHLIGHT_MS);
    }
    return;
  }

  if (target.couplingId) {
    if (!(await waitForCouplings(target.zoneId))) return;
    if (useZoneStore.getState().activeZoneId !== target.zoneId) return;
    const couplings = useCouplingStore.getState();
    const coupling = couplings.couplings.find(c => c.id === target.couplingId);
    const position = coupling ? couplingPosition(coupling) : null;
    if (coupling && position) {
      couplings.setShowTrennstellen(true);
      couplings.selectCoupling(coupling.id);
      ui.setMapView({ lat: position[0], lng: position[1] }, FOCUS_ZOOM);
    }
  }
}

/** Second step of a drill-down: the zone's own monitoring, optionally on a given sub-tab. */
export async function openZoneMonitoring(zoneId: string, subTab?: MonitoringSubTab): Promise<void> {
  // MonitoringPage mounts with its tab and restores the sub-tab from here
  if (subTab) sessionStorage.setItem(MONITORING_SUB_TAB_KEY, subTab);
  await openZone(zoneId, 'monitoring');
}
